// @ts-strict-ignore
import * as React from 'react';
import Icon, { CloseCircleFilled, SettingFilled } from '@ant-design/icons';
import { Event } from '../../utils/analytics';
import { RootState } from '../../reducers/reducers';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    selectAllAppNamesAndPlatformsByAppId,
    selectAllAppsIdsInOrg,
    selectOrg,
    selectUser,
    selectIsWorkflowsCIStepEnabled,
    selectAllAppGroupNamesInOrg,
    selectApp,
    selectOrgGroups,
    selectAIDisabledForOrgInsights,
} from '../../selectors/selectors';
import { selectDestinationsData, selectCanAccessTabularView } from '../../selectors/summaryViewSelectors';
import { getDefaultVersionsForDestinations, getValidVersionForDestinations } from '../../helpers/summaryViewHelpers';
import { OrgComponentProps, OrgSettingsTabType } from '../../typescriptHelpers';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    MehTwoTone,
    SendOutlined,
} from '@ant-design/icons';
import {
    antGrey,
    blurryBackdropStyle,
    colorWithAlpha,
    pageContentStyle,
    runwayColors,
    titleStyle,
} from '../styles/globalStyles';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import {
    AllReleaseTypes,
    OrgOverviewReleaseTypesFilter,
    OrderedStatisticCategory,
    OrgOverviewStatistic,
    ReleaseType,
    StatisticCategory,
    StatisticScope,
    StatisticType,
} from '../../types';
import { PageViewType } from '@/types/summaryView';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Typography from 'antd/lib/typography';
import { OrgOverviewEntityRequest, OrgOverviewFiltersV2, tryFetchEntity } from '../../actions/entities';
import { FetchableEntityType } from '../../entityType';
import { useIsLoading } from '@/utils/hooks/helpers';
import useIsRunwayAdmin from '@/utils/hooks/useIsRunwayAdmin';
import { SummaryTable } from '../orgSummary/SummaryTable';
import { useSummaryView } from '@/utils/hooks/useSummaryView';
import { OverviewTableHeader, PageViewContext } from '../orgSummary/OverviewTableHeader';
import { resolveInitialPreferences, createFilterApplyTracker } from '@/helpers/summaryViewHelpers';
import { trackOrgRouteEvent } from '../../actions/trackEvent';
import { flatten, isEqual } from 'lodash';
import { OrgOverviewAppMetricsTabBar, StatisticCategoryOrAll } from './OrgOverviewAppGroupsTabBar';
import { colorForStatCategory, displayTitleForStatCategory, iconForStatCategory } from './orgOverviewHelpers';
import {
    OrgOverviewDurationsBarGraphStat,
    OrgOverviewReleaseStepDurations,
    OrgOverviewStatCard,
    OrgOverviewStatEmptyCard,
    OrgOverviewStatLoadingCard,
} from './OrgOverviewStatCards';
import { EmptyStateWrapper, featureSectionForStatisticCategory } from '../releaseEmptyState/EmptyStateWrapper';
import { useOrgNavigationFilters } from '../sidebar/OrgOverview/OrgNavigationFiltersContext';
import { Skeleton } from 'antd/lib';
import Alert from 'antd/lib/alert/Alert';
import MarkdownComponent from '../HOCs/Markdown';
import MarkdownParagraphWithEllipsis from '../HOCs/MarkdownParagraphWithEllipsis';
import userflow from 'userflow.js';
import { useLazyGetOrgInsightsQuery, useLazyGetOrgOverviewQuery } from '@/middlewares/defaultApi';
import { Link } from 'react-router-dom';
import { UserSettingsPageView } from '../userSettings/UserSettingsComponent';
import { createOrgSettingsRoute, createUserSettingsRoute } from '@/helpers/routeHelpers';
import Layout, { Content } from 'antd/lib/layout/layout';
import Spin from 'antd/lib/spin';
import { OrgOverviewGridSection, OrgOverviewGridWidget } from './OrgOverviewGridSection';
import {
    OrgOverviewTargetDateSuccessRateStat,
    OrgOverviewStepTargetDateSuccessRateStat,
} from './OrgOverviewTargetDateSuccessRateBarGraph';
import { useShouldShowOrgOverviewDigest } from '@/utils/internalFeatureFlags';
import { AppAndOrgAvatar } from '../buildDistribution/CommonComponents';

const MIN_STAT_CARD_WIDTH = 288;

interface StatCategorySectionProps {
    orgId: string;
    title?: string;
    isLoading: boolean;
    shouldForceEmptyState: boolean;
    stats: OrgOverviewStatistic[];
    category: StatisticCategory;
    scope: StatisticScope;
    isWorkflowsCIStepEnabled: boolean;
}

const StatCategorySection: React.FC<StatCategorySectionProps> = ({
    orgId,
    isLoading,
    shouldForceEmptyState,
    stats,
    title,
    scope,
    category,
    isWorkflowsCIStepEnabled,
}) => {
    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const isEmpty = !isLoading && stats.length === 0;
    const titleIcon = iconForStatCategory(category);
    const gridWidgets = React.useMemo<OrgOverviewGridWidget[]>(() => {
        if (isLoading || isEmpty) {
            return [];
        }

        return stats.map((stat, i) => {
            const widgetId = `stat-${stat.type}`;

            switch (stat.type) {
                case StatisticType.TargetDateSuccessRate:
                    return {
                        id: widgetId,
                        w: 1,
                        h: 3,
                        minW: 1,
                        minH: 3,
                        content: <OrgOverviewTargetDateSuccessRateStat key={i} orgId={orgId} {...stat} scope={scope} />,
                    };
                case StatisticType.TargetDateSuccessRateKickoff:
                case StatisticType.TargetDateSuccessRateSubmission:
                case StatisticType.TargetDateSuccessRateRelease:
                    return {
                        id: widgetId,
                        w: 1,
                        h: 3,
                        minW: 1,
                        minH: 3,
                        content: (
                            <OrgOverviewStepTargetDateSuccessRateStat
                                key={i}
                                orgId={orgId}
                                {...stat}
                                scope={scope}
                                type={stat.type}
                            />
                        ),
                    };
                case StatisticType.StatAverageReleaseStepCompletionTime:
                    return {
                        id: widgetId,
                        w: 1,
                        h: 3,
                        minW: 1,
                        minH: 3,
                        content: (
                            <OrgOverviewReleaseStepDurations
                                key={i}
                                {...stat}
                                scope={scope}
                                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                useNewChartsLibrary
                            />
                        ),
                    };

                case StatisticType.StatReleaseDurations:
                    return {
                        id: widgetId,
                        w: 3,
                        h: 3,
                        minW: 1,
                        minH: 3,
                        content: (
                            <OrgOverviewDurationsBarGraphStat key={i} {...stat} scope={scope} useNewChartsLibrary />
                        ),
                    };
                default:
                    return {
                        id: widgetId,
                        w: 1,
                        h: 3,
                        minW: 1,
                        minH: 3,
                        content: (
                            <OrgOverviewStatCard
                                key={i}
                                orgId={orgId}
                                {...stat}
                                scope={scope}
                                appNamesAndPlatformsByAppId={appNamesAndPlatformsByAppId}
                                useNewChartsLibrary
                            />
                        ),
                    };
            }
        });
    }, [appNamesAndPlatformsByAppId, isEmpty, isLoading, isWorkflowsCIStepEnabled, scope, stats]);

    return (
        <Space direction="vertical" style={{ width: '100%', marginTop: 30 }} size={20}>
            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                }}
            >
                {title && (
                    <>
                        <Typography.Text
                            style={{
                                fontSize: 20,
                                color: antGrey.grey7,
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            {titleIcon && (
                                <Icon
                                    component={titleIcon}
                                    style={{
                                        color: colorForStatCategory(category),
                                    }}
                                />
                            )}
                            {title}
                        </Typography.Text>
                        <div
                            style={{
                                flex: 1,
                                height: '100%',
                            }}
                        >
                            <Divider
                                type="horizontal"
                                style={{
                                    width: '100%',
                                    margin: 0,
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            <EmptyStateWrapper
                featureSections={[featureSectionForStatisticCategory(category)]}
                shouldCenterContent
                shouldForceEnable={isEmpty && shouldForceEmptyState}
                appId={null}
                releaseId={null}
                extraInfo={{
                    scope,
                }}
            >
                {!isLoading && !isEmpty ?
                    <div style={{ width: '100%' }}>
                        <OrgOverviewGridSection
                            key={`${category}-${scope}`}
                            widgets={gridWidgets}
                            storageKey={`${category}-${scope}`}
                        />
                    </div>
                :   <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_STAT_CARD_WIDTH}px, 1fr))`,
                            gap: 20,
                            flex: 1,
                            gridAutoFlow: 'row',
                        }}
                    >
                        {isLoading ?
                            Array(4)
                                .fill(0)
                                .map((_, i) => <OrgOverviewStatLoadingCard key={i} />)
                        : isEmpty ?
                            Array(4)
                                .fill(0)
                                .map((_, i) => <OrgOverviewStatEmptyCard key={i} />)
                        :   null}
                    </div>
                }
            </EmptyStateWrapper>
        </Space>
    );
};

const OrgOverviewV2: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const dispatch = useDispatch();

    const [trackedPageView, setTrackedPageView] = React.useState<string | null>(null);
    const handlePageViewTracking = React.useCallback((pageView: string) => {
        setTrackedPageView((prev) => (prev === pageView ? prev : pageView));
    }, []);

    const [selectedStatCategory, setSelectedStatCategory] = React.useState<StatisticCategoryOrAll>('all');
    const { filters: navigationFilters, setFilters: setNavigationFilters } = useOrgNavigationFilters();
    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId) || {});
    const { selectedUserIds, selectedUserGroupIds } = React.useMemo(() => {
        return Object.keys(navigationFilters.selectedUsers).reduce(
            (acc, selectedId) => {
                if (orgGroupsById[selectedId]) {
                    acc.selectedUserGroupIds.push(selectedId);
                } else {
                    acc.selectedUserIds.push(selectedId);
                }

                return acc;
            },
            { selectedUserIds: [] as string[], selectedUserGroupIds: [] as string[] },
        );
    }, [navigationFilters.selectedUsers, orgGroupsById]);
    const selectedReleaseTypes = React.useMemo<ReleaseType[]>(() => {
        switch (navigationFilters.selectedReleaseTypes) {
            case OrgOverviewReleaseTypesFilter.AllExceptHotfix:
                return AllReleaseTypes.filter((releaseType) => releaseType !== ReleaseType.Hotfix);
            case OrgOverviewReleaseTypesFilter.HotfixOnly:
                return [ReleaseType.Hotfix];
            default:
                return AllReleaseTypes;
        }
    }, [navigationFilters.selectedReleaseTypes]);

    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, props.orgId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, props.orgId);
    const appsById = useSelector((state: RootState) => state.entities.apps.byId);

    React.useEffect(() => {
        if (!orgDestinationsData && !isLoadingOrgDestinations) {
            dispatch(
                tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId: props.orgId }, props.orgId),
            );
        }
    }, [dispatch, props.orgId, orgDestinationsData, isLoadingOrgDestinations]);

    const selectedAppToDestinationIds = React.useMemo(() => {
        const selectedDestinations = navigationFilters.selectedDestinations;
        const selectedWorkflowApps = navigationFilters.selectedWorkflowApps;
        const selectedPlatforms = navigationFilters.selectedPlatforms;
        const hasSelectedDestinations = Object.keys(selectedDestinations).length > 0;
        const hasSelectedWorkflowApps = Object.keys(selectedWorkflowApps).length > 0;
        const hasSelectedPlatforms = Object.keys(selectedPlatforms).length > 0;

        return (orgDestinationsData?.appDestinations ?? []).reduce(
            (acc, { appId, destinations }) => {
                const app = appsById[appId];
                if (!app?.appSettings) {
                    return acc;
                }

                const destinationIds = destinations.reduce((acc, destination) => {
                    if (destination.destinationId) {
                        acc.push(destination.destinationId);
                    }

                    return acc;
                }, [] as string[]);

                if (!hasSelectedDestinations && !hasSelectedWorkflowApps && !hasSelectedPlatforms) {
                    acc[appId] = destinationIds;
                    return acc;
                }

                const matchesSelectedWorkflowApps = !hasSelectedWorkflowApps || !!selectedWorkflowApps[appId];
                const matchesSelectedPlatforms = !hasSelectedPlatforms || !!selectedPlatforms[app.appSettings.platform];
                const selectedDestinationIdsForApp = destinationIds.filter(
                    (destinationId) => !!selectedDestinations[destinationId],
                );
                const hasLegacyAppSelection = !!selectedDestinations[appId];
                const shouldIncludeAppFromWorkflowOrPlatformFilters =
                    (hasSelectedWorkflowApps && matchesSelectedWorkflowApps) ||
                    (hasSelectedPlatforms && matchesSelectedPlatforms);

                if (hasSelectedDestinations && (selectedDestinationIdsForApp.length || hasLegacyAppSelection)) {
                    acc[appId] = selectedDestinationIdsForApp;
                }

                if (shouldIncludeAppFromWorkflowOrPlatformFilters) {
                    acc[appId] = destinationIds;
                }

                return acc;
            },
            {} as Record<string, string[]>,
        );
    }, [
        appsById,
        navigationFilters.selectedDestinations,
        navigationFilters.selectedPlatforms,
        navigationFilters.selectedWorkflowApps,
        orgDestinationsData?.appDestinations,
    ]);
    const destinationIdsByAppId = React.useMemo(
        () =>
            (orgDestinationsData?.appDestinations ?? []).reduce(
                (acc, { appId, destinations }) => {
                    acc[appId] = (destinations ?? []).reduce((ids, destination) => {
                        if (destination.destinationId) {
                            ids.push(destination.destinationId);
                        }
                        return ids;
                    }, [] as string[]);
                    return acc;
                },
                {} as Record<string, string[]>,
            ),
        [orgDestinationsData?.appDestinations],
    );

    const [selectedStatFilters, setSelectedStatFilters] = React.useState<OrgOverviewFiltersV2>({
        orgId: props.orgId,
        appToDestinationIds: selectedAppToDestinationIds,
        userIds: selectedUserIds,
        userGroupIds: selectedUserGroupIds,
        selectedMetrics: navigationFilters.selectedMetrics as StatisticType[],
        startDate: navigationFilters.selectedDateRange.start,
        endDate: navigationFilters.selectedDateRange.end,
        releaseTypes: selectedReleaseTypes,
    });
    const lastSubmittedFiltersRef = React.useRef<OrgOverviewFiltersV2 | null>(null);
    const [fetchOrgOverview, { data: orgOverviewResponse, isLoading, isFetching }] = useLazyGetOrgOverviewQuery();
    const lastOrgOverviewRequestRef = React.useRef<ReturnType<typeof fetchOrgOverview> | null>(null);

    const isAIDisabled = useSelector((state: RootState) => selectAIDisabledForOrgInsights(state, props.orgId));
    const [fetchOrgInsights, { data: orgInsightsResponse, isLoading: isLoadingInsights }] =
        useLazyGetOrgInsightsQuery();
    const fetchedInsightsOrgIdsRef = React.useRef(new Set<string>());

    const { orgId, orgName, orgImageUrl } = useSelector((state: RootState) => {
        const org = selectOrg(state, props.orgId);
        return {
            orgId: org.orgId,
            orgName: org.name,
            orgImageUrl: org.orgImageUrl,
        };
    }, shallowEqual);

    const canAccessTabularView = useSelector((state: RootState) => selectCanAccessTabularView(state, props.orgId));

    // TEST ONLY — remove this block (and the useIsRunwayAdmin import) once tabular view testing is complete.
    // Mirrors the same gate in AppHomeComponent.
    const TABULAR_VIEW_TEST_ORG_ID = 'doordash-storefront';
    const isRunwayAdmin = useIsRunwayAdmin();
    const canSeeTabularView = canAccessTabularView && (orgId !== TABULAR_VIEW_TEST_ORG_ID || isRunwayAdmin);
    // END TEST ONLY

    React.useEffect(() => {
        if (!canSeeTabularView) {
            handlePageViewTracking(PageViewType.Default);
        }
    }, [canSeeTabularView, handlePageViewTracking]);

    const selectedStatFilterAppIds = Object.keys(selectedStatFilters?.appToDestinationIds ?? {});
    const hasSelectedMetrics = (selectedStatFilters?.selectedMetrics?.length ?? 0) > 0;
    const singleAppId = selectedStatFilterAppIds.length === 1 ? selectedStatFilterAppIds[0] : null;
    const singleAppSelected = useSelector((state: RootState) => (singleAppId ? selectApp(state, singleAppId) : null));
    const orgOverview = orgOverviewResponse?.data;

    const statAppIds = Object.keys(selectedAppToDestinationIds ?? {});
    const isWaitingForInitialOrgOverviewFilters =
        hasSelectedMetrics && !selectedStatFilterAppIds.length && !lastSubmittedFiltersRef.current;
    const isOrgOverviewLoading = isLoading || isFetching || isWaitingForInitialOrgOverviewFilters;
    const overviewImageUrl = orgImageUrl;
    const overviewTitle = orgName;
    const selectedHeaderAppName = singleAppSelected?.appSettings.appName;
    const selectedHeaderAppImageUrl = singleAppSelected?.appSettings.appImageUrl;

    const clearSelectedHeaderApp = React.useCallback(() => {
        if (!singleAppId) {
            return;
        }

        setNavigationFilters((currentFilters) => {
            const nextWorkflowApps = { ...currentFilters.selectedWorkflowApps };
            const nextDestinations = { ...currentFilters.selectedDestinations };

            delete nextWorkflowApps[singleAppId];
            delete nextDestinations[singleAppId];
            (destinationIdsByAppId[singleAppId] ?? []).forEach((destinationId) => {
                delete nextDestinations[destinationId];
            });

            return {
                ...currentFilters,
                selectedWorkflowApps: nextWorkflowApps,
                selectedDestinations: nextDestinations,
            };
        });
    }, [destinationIdsByAppId, singleAppId, setNavigationFilters]);

    React.useEffect(() => {
        setSelectedStatFilters({
            orgId: props.orgId,
            appToDestinationIds: selectedAppToDestinationIds,
            userIds: selectedUserIds,
            userGroupIds: selectedUserGroupIds,
            selectedMetrics: navigationFilters.selectedMetrics as StatisticType[],
            startDate: navigationFilters.selectedDateRange.start,
            endDate: navigationFilters.selectedDateRange.end,
            releaseTypes: selectedReleaseTypes,
        });
    }, [
        props.orgId,
        navigationFilters.selectedDateRange.end,
        navigationFilters.selectedDateRange.start,
        navigationFilters.selectedMetrics,
        navigationFilters.selectedReleaseTypes,
        navigationFilters.selectedUsers,
        selectedAppToDestinationIds,
        selectedReleaseTypes,
        selectedUserGroupIds,
        selectedUserIds,
    ]);

    React.useEffect(() => {
        if (!trackedPageView) return;
        dispatch(
            trackOrgRouteEvent({
                orgId: props.orgId,
                eventName: Event.ViewOrgOverview,
                viewType: trackedPageView,
            }),
        );
    }, [dispatch, props.orgId, trackedPageView]);

    React.useEffect(() => {
        if (
            !navigationFilters.shouldGenerateInsights ||
            fetchedInsightsOrgIdsRef.current.has(props.orgId) ||
            !isAIDisabled
        ) {
            return;
        }

        fetchedInsightsOrgIdsRef.current.add(props.orgId);
        void fetchOrgInsights({ orgId: props.orgId }, true);
    }, [fetchOrgInsights, navigationFilters.shouldGenerateInsights, props.orgId]);

    const scope: StatisticScope = StatisticScope.Org;

    const statAppIdsKey = React.useMemo(() => statAppIds.slice().sort().join(','), [statAppIds]);

    React.useEffect(() => {
        if (lastSubmittedFiltersRef.current && isEqual(lastSubmittedFiltersRef.current, selectedStatFilters)) {
            return;
        }

        if (!selectedStatFilterAppIds.length) {
            return;
        }

        if (!selectedStatFilters.selectedMetrics?.length) {
            lastOrgOverviewRequestRef.current?.abort();
            return;
        }

        const request: OrgOverviewEntityRequest<OrgOverviewFiltersV2> = {
            orgId: props.orgId,
            filters: selectedStatFilters,
            appIds: statAppIds,
        };

        if (!request.appIds.length) {
            return;
        }

        lastOrgOverviewRequestRef.current?.abort();
        lastSubmittedFiltersRef.current = selectedStatFilters;
        lastOrgOverviewRequestRef.current = fetchOrgOverview(request, true);
    }, [
        hasSelectedMetrics,
        selectedStatFilterAppIds.length,
        selectedStatFilters,
        fetchOrgOverview,
        props.orgId,
        statAppIds,
        statAppIdsKey,
    ]);

    const visibleStatistics = orgOverview?.statistics ?? [];

    const statisticsByType =
        (visibleStatistics.reduce((acc, stat) => {
            acc[stat.type] = [...(acc[stat.type] ?? []), stat];
            return acc;
        }, {}) as Record<string, OrgOverviewStatistic[]>) ?? {};

    const allStatCategories = Object.entries(OrderedStatisticCategory)
        .sort((a, b) => a[1] - b[1])
        .map(([category]) => category as StatisticCategory);

    const statistics = flatten(Object.values(statisticsByType));
    const hasNoMetricsAcrossAllCategories = !isOrgOverviewLoading && statistics.length === 0;

    const availableStatCategories =
        isOrgOverviewLoading || hasNoMetricsAcrossAllCategories ? allStatCategories : (
            allStatCategories.filter((category) => statistics.some((statistic) => statistic.category === category))
        );

    const statisticsByCategory = statistics
        .filter((statistic) => statistic.category === selectedStatCategory || selectedStatCategory === 'all')
        .reduce(
            (acc, statistic) => ({
                ...acc,
                [statistic.category]: [...(acc[statistic.category] ?? []), statistic],
            }),
            {} as Record<StatisticCategory, OrgOverviewStatistic[]>,
        );

    const shouldShowAllCategories = selectedStatCategory === 'all';

    const categoriesToRender =
        shouldShowAllCategories ? availableStatCategories : [selectedStatCategory as StatisticCategory];

    React.useEffect(() => {
        if (selectedStatCategory === 'all') {
            return;
        }

        if (!availableStatCategories.includes(selectedStatCategory as StatisticCategory)) {
            setSelectedStatCategory('all');
        }
    }, [availableStatCategories, selectedStatCategory]);

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, singleAppId),
    );

    const insightsAlert = orgInsightsResponse?.data;

    const getInsightsAlertType = (): 'success' | 'error' | 'warning' | 'info' => {
        return insightsAlert.type;
    };

    const getInsightsAlertColor = (): string => {
        const alertType = getInsightsAlertType();
        switch (alertType) {
            case 'success':
                return runwayColors.statusGreen;
            case 'error':
                return runwayColors.statusRed;
            case 'warning':
                return runwayColors.statusYellow;
            case 'info':
                return colorWithAlpha(runwayColors.primary, 0.25);
            default:
                return null;
        }
    };

    const getInsightsAlertIcon = (): React.ReactNode => {
        const alertType = getInsightsAlertType();
        const iconStyle = { fontSize: 18 };
        switch (alertType) {
            case 'success':
                return <CheckCircleOutlined style={iconStyle} />;
            case 'error':
                return <CloseCircleOutlined style={iconStyle} />;
            case 'warning':
                return <ExclamationCircleOutlined style={iconStyle} />;
            case 'info':
                return <MehTwoTone style={iconStyle} />;
            default:
                return null;
        }
    };

    const showUserflowInsightsFeedback = () => {
        userflow.start('9bcc579e-33ba-4018-821a-727301341e41');
    };

    const renderChartsView = () => (
        <>
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    padding: '0 32px',
                    marginLeft: -32,
                    paddingTop: 32,
                    zIndex: 2,
                    width: 'calc(100% + 64px)',
                    ...blurryBackdropStyle,
                }}
            >
                <OrgOverviewAppMetricsTabBar
                    selectedStatCategory={selectedStatCategory}
                    availableStatCategories={availableStatCategories}
                    onStatCategorySelected={(statCategory) => {
                        setSelectedStatCategory(statCategory);
                    }}
                />
            </div>

            {navigationFilters.shouldGenerateInsights && insightsAlert ?
                <Alert
                    type={getInsightsAlertType()}
                    closable
                    onClose={() =>
                        setNavigationFilters((currentFilters) => ({
                            ...currentFilters,
                            shouldGenerateInsights: false,
                        }))
                    }
                    style={{
                        width: 800,
                        marginTop: 20,
                        padding: 19,
                        border: `1px solid ${getInsightsAlertColor()}`,
                        borderRadius: 0,
                    }}
                    message={
                        isLoadingInsights ?
                            <Skeleton
                                active
                                round={false}
                                style={{
                                    padding: 12,
                                }}
                                paragraph={{
                                    rows: 3,
                                }}
                            />
                        :   <Space style={{ alignItems: 'flex-start' }} size={16}>
                                {getInsightsAlertIcon()}
                                <div>
                                    <MarkdownComponent source={insightsAlert.title} style={{ fontSize: 16 }} />
                                    <MarkdownParagraphWithEllipsis
                                        content={insightsAlert.description}
                                        maxHeight="250px"
                                        style={{ fontSize: 14, marginBottom: 8 }}
                                        backgroundColor="transparent"
                                    />
                                    <Text type="secondary" style={{ marginTop: 8 }}>
                                        AI insights are in beta.{' '}
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={showUserflowInsightsFeedback}
                                            style={{ padding: 0 }}
                                        >
                                            Send feedback?
                                        </Button>
                                    </Text>
                                </div>
                            </Space>
                    }
                />
            :   null}

            <div
                style={{
                    position: 'relative',
                    marginBottom: 56,
                }}
            >
                {categoriesToRender.map((category) => {
                    const stats: OrgOverviewStatistic[] = statisticsByCategory[category] ?? [];
                    return (
                        <StatCategorySection
                            key={category}
                            orgId={props.orgId}
                            category={category}
                            title={shouldShowAllCategories ? displayTitleForStatCategory(category) : null}
                            isLoading={isOrgOverviewLoading}
                            shouldForceEmptyState={hasNoMetricsAcrossAllCategories}
                            stats={stats}
                            scope={scope}
                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                        />
                    );
                })}
            </div>
        </>
    );

    return (
        <Layout
            style={{
                ...pageContentStyle,
                paddingTop: 0,
                paddingBottom: 0,
            }}
        >
            <Content
                style={{
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    paddingLeft: 32,
                    paddingRight: 32,
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        zIndex: 1,
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            paddingTop: 24,
                            alignItems: 'flex-start',
                            gap: 16,
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            {singleAppSelected ?
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        border: `1px solid ${runwayColors.primary}`,
                                        backgroundColor: antGrey.grey2,
                                        borderRadius: 16,
                                        padding: 8,
                                        width: 800,
                                        minHeight: 72,
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                        <AppAndOrgAvatar url={selectedHeaderAppImageUrl} name={selectedHeaderAppName} />
                                        <div style={{ minWidth: 0 }}>
                                            <Text
                                                style={{
                                                    fontSize: 24,
                                                    fontWeight: 600,
                                                    color: antGrey.grey8,
                                                    display: 'block',
                                                }}
                                            >
                                                {selectedHeaderAppName}
                                            </Text>
                                            <Text
                                                style={{
                                                    ...titleStyle,
                                                    color: antGrey.grey6,
                                                    letterSpacing: '0.16em',
                                                }}
                                            >
                                                Overview
                                            </Text>
                                        </div>
                                    </div>
                                    <Button
                                        type="text"
                                        shape="circle"
                                        icon={<CloseCircleFilled style={{ color: antGrey.grey5, fontSize: 28 }} />}
                                        onClick={clearSelectedHeaderApp}
                                    />
                                </div>
                            :   <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <AppAndOrgAvatar url={overviewImageUrl} name={overviewTitle} />
                                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                        <Text
                                            style={{
                                                fontSize: 24,
                                                marginBottom: 6,
                                                fontWeight: 600,
                                                color: antGrey.grey8,
                                            }}
                                        >
                                            {overviewTitle}
                                        </Text>
                                        <Text style={{ ...titleStyle, color: antGrey.grey6 }}>Overview</Text>
                                    </div>
                                </div>
                            }
                        </div>
                        {useShouldShowOrgOverviewDigest() ?
                            <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                                <Button icon={<SendOutlined style={{ color: runwayColors.primary }} />}>
                                    Manage subscriptions...
                                </Button>
                            </Link>
                        :   <Link to={createOrgSettingsRoute(orgId, OrgSettingsTabType.Team)}>
                                <Button icon={<SettingFilled style={{ color: runwayColors.primary }} />}>
                                    Settings
                                </Button>
                            </Link>
                        }
                    </div>
                </div>
                {isLoadingOrgDestinations ?
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Spin size="large" />
                        <Text style={{ color: runwayColors.primary, paddingTop: 10 }}>Loading...</Text>
                    </div>
                : canSeeTabularView ?
                    <div style={{ marginTop: 32 }}>
                        <OrgOverviewWorkflowsContent
                            orgId={orgId}
                            renderChartsView={renderChartsView}
                            onPageViewChange={handlePageViewTracking}
                        />
                    </div>
                :   renderChartsView()}
            </Content>
        </Layout>
    );
};

export default OrgOverviewV2;

interface OrgOverviewWorkflowsContentProps {
    orgId: string;
    renderChartsView: () => React.ReactNode;
    onPageViewChange: (pageView: string) => void;
}

const OrgOverviewWorkflowsContent: React.FC<OrgOverviewWorkflowsContentProps> = ({
    orgId,
    renderChartsView,
    onPageViewChange,
}) => {
    const summary = useSummaryView({ orgId });
    const {
        preferences,
        loading: { isLoading: isLoadingSummaryView, isLoadingInitialData: isLoadingOrgDestinations },
        summaryViewResponse,
        availableVersions,
        availableDestinationIds,
        availableMetrics,
        fetchSummaryViewBatched,
        initializeWithDefaults,
        pagination: { destinationLimit, setLoadedDestinationIds },
        updatePreferences,
    } = summary;

    const showCharts = preferences.pageView === PageViewType.Default;
    const isReleasesTabSelected = preferences.pageView === PageViewType.Tabular;

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const user = useSelector((state: RootState) => selectUser(state));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));
    const destinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));

    const handleTrackFilterApply = React.useMemo(
        () => createFilterApplyTracker(org, user, appsInOrg, appGroupsInOrg, preferences, PageViewContext.OrgOverview),
        [org, user, appsInOrg, appGroupsInOrg, preferences],
    );

    React.useEffect(() => {
        const { pageView, tabularLayout } = preferences;
        const viewType = pageView === PageViewType.Default ? PageViewType.Default : `${pageView}-${tabularLayout}`;
        onPageViewChange(viewType);
    }, [onPageViewChange, preferences.pageView, preferences.tabularLayout]);

    const initialDestinationIds = React.useMemo(() => {
        if (availableDestinationIds.length === 0) return [];
        return destinationLimit ? availableDestinationIds.slice(0, destinationLimit) : availableDestinationIds;
    }, [availableDestinationIds, destinationLimit]);

    const defaultVersions = React.useMemo(() => {
        const validVersion = getValidVersionForDestinations(initialDestinationIds, destinationsData);
        if (validVersion) return [validVersion];
        return getDefaultVersionsForDestinations(availableVersions);
    }, [initialDestinationIds, destinationsData, availableVersions]);

    React.useEffect(() => {
        if (!isReleasesTabSelected) return;
        if (isLoadingOrgDestinations) return;
        if (!destinationLimit) return;
        if (initialDestinationIds.length === 0 || availableVersions.length === 0) return;

        initializeWithDefaults({
            versions: defaultVersions,
            destinationIds: initialDestinationIds,
        });
    }, [
        isLoadingOrgDestinations,
        destinationLimit,
        initialDestinationIds,
        availableVersions.length,
        defaultVersions,
        initializeWithDefaults,
        isReleasesTabSelected,
    ]);

    const allMetricKeys = React.useMemo(() => availableMetrics.map((m) => m.key), [availableMetrics]);

    const hasInitialFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (!isReleasesTabSelected) return;
        if (isLoadingOrgDestinations) return;
        if (!destinationLimit) return;
        if (isLoadingSummaryView) return;
        if (summaryViewResponse) return;
        if (hasInitialFetchedRef.current) return;

        const resolved = resolveInitialPreferences(
            summary.getStoredPreferences(),
            { versions: availableVersions, destinationIds: availableDestinationIds, metricKeys: allMetricKeys },
            { versions: defaultVersions, destinationIds: initialDestinationIds, metrics: allMetricKeys },
        );

        if (resolved.destinationIds.length === 0 || resolved.versions.length === 0) return;

        hasInitialFetchedRef.current = true;

        setLoadedDestinationIds(resolved.destinationIds);
        updatePreferences({ destinations: resolved.destinationIds });
        fetchSummaryViewBatched({
            destinationIds: resolved.destinationIds,
            versions: resolved.versions,
            properties: resolved.metrics,
        });
    }, [
        isLoadingOrgDestinations,
        isLoadingSummaryView,
        summaryViewResponse,
        availableDestinationIds,
        availableVersions,
        allMetricKeys,
        initialDestinationIds,
        defaultVersions,
        destinationLimit,
        isReleasesTabSelected,
        summary.getStoredPreferences,
        fetchSummaryViewBatched,
        setLoadedDestinationIds,
        updatePreferences,
    ]);

    return (
        <>
            <OverviewTableHeader
                context={PageViewContext.OrgOverview}
                summary={summary}
                onTrackFilterApply={handleTrackFilterApply}
            />
            {showCharts ? renderChartsView() : <SummaryTable summary={summary} />}
        </>
    );
};
